import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import axios from "axios";
import {
  FaArrowLeft,
  FaTicketAlt,
  FaPlus,
  FaSearch,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaClock,
  FaBan,
  FaChevronRight,
  FaFilter,
  FaRedo,
} from "react-icons/fa";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const Tickets = () => {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  const fetchProjects = async () => {
    setProjectsLoading(true);

    try {
        const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/projects/getallowed`,
        {
            headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
            },
        },
        );

        setProjects(response.data.projects || []);
    } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
    } finally {
        setProjectsLoading(false);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tickets`,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        },
      );

      console.log(response.data.tickets);

      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchTickets();
  }, []);

  const filteredTickets = useMemo(() => {
    let result = [...tickets];

    if (statusFilter !== "all") {
      result = result.filter(
        (ticket) => ticket.status === statusFilter,
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();

      result = result.filter((ticket) => {
        const ticketNumber = String(ticket.ticket_number || "");
        const projectName = (ticket.project_name || "").toLowerCase();

        return (
          ticketNumber.includes(query) ||
          projectName.includes(query)
        );
      });
    }

    return result;
  }, [tickets, statusFilter, searchQuery]);

  const stats = useMemo(() => {
    return {
      total: tickets.length,
      pending: tickets.filter((t) => t.status === "pending").length,
      won: tickets.filter((t) => t.status === "won").length,
      lost: tickets.filter((t) => t.status === "lost").length,
      cancelled: tickets.filter((t) => t.status === "cancelled").length,
    };
  }, [tickets]);

  const handleNewTicket = () => {
    if (!selectedProject) {
        alert("Please select a project first.");
        return;
    }

    navigate("/tickets/new", {
        state: {
        project: selectedProject,
        },
    });
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "won":
        return {
          label: "Won",
          className:
            "bg-green-900/40 text-green-400 border-green-500/30",
          icon: <FaCheck />,
        };

      case "lost":
        return {
          label: "Lost",
          className:
            "bg-red-900/40 text-red-400 border-red-500/30",
          icon: <FaTimes />,
        };

      case "cancelled":
        return {
          label: "Cancelled",
          className:
            "bg-gray-700/60 text-gray-400 border-gray-600/30",
          icon: <FaBan />,
        };

      default:
        return {
          label: "Pending",
          className:
            "bg-blue-900/40 text-blue-400 border-blue-500/30",
          icon: <FaClock />,
        };
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    try {
      return new Date(date).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined) return "-";

    return Number(value).toFixed(2);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="inline-block animate-spin text-4xl text-green-400 mb-4" />
            <p className="text-gray-300">Loading tickets...</p>
            <p className="text-gray-500 text-sm mt-2">
              Please wait
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto p-4 md:p-6">

        {/* Back */}
        <button
          onClick={() => navigate("/projects")}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-5 transition-colors"
        >
          <FaArrowLeft />
          Back to Projects
        </button>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                <FaTicketAlt className="text-green-400" />

                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                    My Tickets
                </span>
                </h1>

                <p className="text-gray-400 text-sm mt-1">
                Keep track of your bets across all your projects
                </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                {/* Project selector */}
                <select
                value={selectedProject?.id || ""}
                onChange={(e) => {
                    const project = projects.find(
                    (p) => String(p.id) === String(e.target.value)
                    );

                    setSelectedProject(project || null);
                }}
                disabled={projectsLoading}
                className="min-w-[220px] px-4 py-2.5 rounded-lg bg-gray-700/80 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                <option value="">
                    {projectsLoading ? "Loading projects..." : "Select project"}
                </option>

                {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                    {project.name}
                    </option>
                ))}
                </select>

                <button
                onClick={handleNewTicket}
                disabled={!selectedProject}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    selectedProject
                    ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                }`}
                >
                <FaPlus className="inline mr-2" />
                New Ticket
                </button>
            </div>
        </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">

            <div className="bg-gray-800/60 border border-green-500/20 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase">
                Total
              </p>
              <p className="text-2xl font-bold text-white mt-1">
                {stats.total}
              </p>
            </div>

            <div className="bg-gray-800/60 border border-blue-500/20 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase">
                Pending
              </p>
              <p className="text-2xl font-bold text-blue-400 mt-1">
                {stats.pending}
              </p>
            </div>

            <div className="bg-gray-800/60 border border-green-500/20 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase">
                Won
              </p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {stats.won}
              </p>
            </div>

            <div className="bg-gray-800/60 border border-red-500/20 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase">
                Lost
              </p>
              <p className="text-2xl font-bold text-red-400 mt-1">
                {stats.lost}
              </p>
            </div>

            <div className="bg-gray-800/60 border border-gray-500/20 rounded-xl p-4">
              <p className="text-gray-400 text-xs uppercase">
                Cancelled
              </p>
              <p className="text-2xl font-bold text-gray-400 mt-1">
                {stats.cancelled}
              </p>
            </div>

          </div>

          {/* Filters */}
          <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl p-4 mb-6">

            <div className="flex flex-col lg:flex-row gap-3">

              {/* Search */}
              <div className="relative flex-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) =>
                    setSearchQuery(e.target.value)
                  }
                  placeholder="Search ..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-700/60 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Status */}
              <div className="flex flex-wrap gap-2">

                {[
                  ["all", "All"],
                  ["pending", "Pending"],
                  ["won", "Won"],
                  ["lost", "Lost"],
                  ["cancelled", "Cancelled"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setStatusFilter(value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      statusFilter === value
                        ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                        : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}

              </div>

              {(searchQuery || statusFilter !== "all") && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 rounded-lg bg-gray-700/60 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FaTimes />
                  Clear
                </button>
              )}

            </div>
          </div>

          {/* Tickets */}
          {filteredTickets.length > 0 ? (
            <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden">

              {/* Desktop */}
              <div className="overflow-x-auto">
                <table className="w-full hidden md:table">

                  <thead className="bg-gray-700/40">
                    <tr>
                      <th className="text-left px-5 py-4 text-sm text-gray-300 font-medium">
                        Ticket
                      </th>

                      <th className="text-left px-5 py-4 text-sm text-gray-300 font-medium">
                        Project
                      </th>

                      <th className="text-center px-5 py-4 text-sm text-gray-300 font-medium">
                        Stake
                      </th>

                      <th className="text-center px-5 py-4 text-sm text-gray-300 font-medium">
                        Total Odd
                      </th>

                      <th className="text-center px-5 py-4 text-sm text-gray-300 font-medium">
                        Potential Return
                      </th>

                      <th className="text-center px-5 py-4 text-sm text-gray-300 font-medium">
                        Status
                      </th>

                      <th className="text-center px-5 py-4 text-sm text-gray-300 font-medium">
                        Date
                      </th>

                      <th />
                    </tr>
                  </thead>

                  <tbody>

                    {filteredTickets.map((ticket) => {
                      const status = getStatusInfo(ticket.status);

                      return (
                        <tr
                          key={ticket.id}
                          onClick={() =>
                            navigate(`/tickets/${ticket.id}`)
                          }
                          className="border-t border-green-500/10 hover:bg-gray-700/30 transition-colors cursor-pointer"
                        >

                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-green-900/40 flex items-center justify-center">
                                <FaTicketAlt className="text-green-400" />
                              </div>

                              <div>
                                <p className="text-white font-medium">
                                  Ticket #
                                  {ticket.ticket_number ||
                                    ticket.id}
                                </p>

                                <p className="text-xs text-gray-500">
                                  ID: {ticket.id}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            <span className="text-gray-300">
                              {ticket.project_name ||
                                "No project"}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-center">
                            <span className="text-white font-medium">
                              {formatMoney(ticket.stake)}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-center">
                            <span className="text-green-400 font-semibold">
                              {Number(ticket.total_odd || 0).toFixed(2)}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-center">
                            <span className="text-white font-medium">
                              {formatMoney(
                                ticket.potential_return,
                              )}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-center">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${status.className}`}
                            >
                              {status.icon}
                              {status.label}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-center text-sm text-gray-400">
                            {formatDate(ticket.created_at)}
                          </td>

                          <td className="px-4 py-4 text-right">
                            <FaChevronRight className="text-gray-500" />
                          </td>

                        </tr>
                      );
                    })}

                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="md:hidden divide-y divide-green-500/10">

                {filteredTickets.map((ticket) => {
                  const status = getStatusInfo(ticket.status);

                  return (
                    <div
                      key={ticket.id}
                      onClick={() =>
                        navigate(`/tickets/${ticket.id}`)
                      }
                      className="p-4 hover:bg-gray-700/30 transition-colors cursor-pointer"
                    >

                      <div className="flex items-center justify-between mb-3">

                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-green-900/40 flex items-center justify-center">
                            <FaTicketAlt className="text-green-400" />
                          </div>

                          <div>
                            <p className="text-white font-medium">
                              Ticket #
                              {ticket.ticket_number ||
                                ticket.id}
                            </p>

                            <p className="text-xs text-gray-500">
                              {formatDate(ticket.created_at)}
                            </p>
                          </div>
                        </div>

                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${status.className}`}
                        >
                          {status.icon}
                          {status.label}
                        </span>

                      </div>

                      <div className="grid grid-cols-3 gap-3">

                        <div>
                          <p className="text-xs text-gray-500">
                            Stake
                          </p>
                          <p className="text-sm text-white font-medium">
                            {formatMoney(ticket.stake)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">
                            Odd
                          </p>
                          <p className="text-sm text-green-400 font-semibold">
                            {Number(ticket.total_odd || 0).toFixed(2)}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">
                            Return
                          </p>
                          <p className="text-sm text-white font-medium">
                            {formatMoney(
                              ticket.potential_return,
                            )}
                          </p>
                        </div>

                      </div>

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700/50">
                        <span className="text-xs text-gray-400">
                          {ticket.project_name ||
                            "No project"}
                        </span>

                        <FaChevronRight className="text-gray-500 text-xs" />
                      </div>

                    </div>
                  );
                })}

              </div>

            </div>
          ) : (
            <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl p-10 text-center">

              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-700/60 flex items-center justify-center">
                <FaTicketAlt className="text-2xl text-gray-500" />
              </div>

              <h3 className="text-lg font-semibold text-white">
                No tickets found
              </h3>

            <p className="text-gray-400 text-sm mt-2">
                {tickets.length === 0
                    ? "Select a project above and create your first ticket."
                    : "Try adjusting your filters."}
            </p>

              {tickets.length === 0 ? (
                <button
                onClick={handleNewTicket}
                disabled={!selectedProject}
                className={`mt-5 px-5 py-2.5 rounded-xl font-medium transition-all ${
                    selectedProject
                    ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
                    : "bg-gray-700 text-gray-500 cursor-not-allowed"
                }`}
                >
                <FaPlus className="inline mr-2" />
                Create Ticket
                </button>
              ) : (
                <button
                  onClick={clearFilters}
                  className="mt-5 px-5 py-2.5 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                >
                  Clear Filters
                </button>
              )}

            </div>
          )}

          {/* Refresh */}
          <div className="flex justify-center mt-6">
            <button
              onClick={fetchTickets}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-green-400 transition-colors"
            >
              <FaRedo />
              Refresh tickets
            </button>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default Tickets;